import React, {useEffect} from 'react';
import {
    useDashboardContext,
    DashboardProvider,
} from 'contexts/DashboardContext';
import {Col, Row} from 'react-bootstrap';
import {Loader} from 'shared/helpers';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {useTimeOfDay, useDashboardTableConfig} from 'hooks';
import {DashboardJobList} from 'components/customer/Dashboard/DashboardJobList';
import {UpdateCenter} from 'components/customer/Dashboard/UpdateCenter';
import {
    MobileMenu,
    MobileRender,
    MobileContainerDecorator,
} from 'components/customer/Dashboard/MobileMenu';
import {LeadTimes} from 'components/customer/Dashboard/LeadTimes';
import {LastJobButton} from 'components/customer/Dashboard/LastJobButton';
import styled from 'styled-components';

import './Dashboard.scss';
import {usePreCacheProducts} from 'hooks/preCacheProducts';
import {AddJob} from 'components/customer/Job/AddJob';

export const DashboardContents = (): React.ReactElement => {
    const isSmallDevice = isDeviceSmall();
    const timeOfDay: string = useTimeOfDay();

    const {
        isUpdateCenterJobsVisibleMobile,
        isLeadTimeVisibleMobile,
        setIsQuotedJobsVisibleMobile,
        setIsUpdateCenterJobsVisibleMobile,
    } = useDashboardContext();

    // start fetching products in background as soon as customer
    // lands on the dashboard
    usePreCacheProducts();

    const {loader, userName, jobs, dialog, isNewCustomer} =
        useDashboardTableConfig();

    useEffect(() => {
        if (
            typeof jobs?.quotedJobs !== 'undefined' &&
            jobs?.quotedJobs?.length > 0
        ) {
            setIsQuotedJobsVisibleMobile(true);
            setIsUpdateCenterJobsVisibleMobile(false);
        }
    }, [jobs]);

    return (
        <>
            <Loader loader={loader} hideInitially={true} />
            <div className="dashboard">
                <Row>
                    <Col
                        className="greeting"
                        lg={isNewCustomer ? {span: 6, offset: 3} : 7}
                        md={isNewCustomer ? {span: 6, offset: 3} : 6}
                        xs={12}>
                        <Row>
                            <Col md={12}>
                                <Jumbotron className="jumbotron">
                                    <h1>
                                        Good {timeOfDay}, {userName}
                                    </h1>
                                    <section>
                                        <div>
                                            <AddJob />
                                        </div>
                                        <LastJobButton />
                                    </section>
                                </Jumbotron>
                            </Col>
                            <Col className="lead-times">
                                <MobileRender
                                    isVisible={isLeadTimeVisibleMobile}>
                                    <LeadTimes />
                                </MobileRender>
                            </Col>
                        </Row>
                    </Col>
                    <Col
                        className="update-center"
                        lg={isNewCustomer ? {span: 6, offset: 3} : 5}
                        md={isNewCustomer ? {span: 6, offset: 3} : 6}
                        xs={12}>
                        <MobileRender
                            isVisible={isUpdateCenterJobsVisibleMobile}>
                            <UpdateCenter />
                        </MobileRender>
                    </Col>
                </Row>

                <DashboardJobListRow $isMobile={isSmallDevice}>
                    {jobs ? <DashboardJobList jobs={jobs} /> : null}
                </DashboardJobListRow>

                {isSmallDevice ? <MobileMenu /> : null}
                <br />
                {dialog}
            </div>
        </>
    );
};

// eslint-disable-next-line react/no-multi-comp -- lets allow multi component here
export const Dashboard = () => {
    return (
        <DashboardProvider>
            <MobileContainerDecorator>
                <DashboardContents />
            </MobileContainerDecorator>
        </DashboardProvider>
    );
};

const DashboardJobListRow = styled(Row)<{$isMobile?: boolean}>`
    ${({$isMobile}) => ($isMobile ? 'margin: 0;' : '')}
`;

const Jumbotron = styled.div`
    background: #fff;
    margin: 0;
    padding: 2.5rem 0;
`;
